import { Roles } from './roleEnums';
import { getAppEnvironment } from '../config/runtimeConfig';

/**
 * Yetki kontrolü için strategy pattern implementasyonu
 * Her yetki türü için ayrı strategy sınıfı
 */

// Base Strategy Interface
class PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        throw new Error('check method must be implemented');
    }
}

// Kmobility User Strategy
class KmobilityUserStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        return userParams?.isKmobilityUser;
    }
}

//CanUseFullService Strategy
class CanUseFullServiceStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        return userParams?.isCanUseFullServiceDealer;
    }
}

// Warranty Team Roles - Export for reuse
export const warrantyTeamRoles = [
    Roles.WARRANTY_TEAM,
    Roles.WARRANTY_MANAGER,
    Roles.SSH_MANAGER,
    Roles.SSH_DIRECTOR,
    Roles.SSH_GENERAL_MANAGER,
    Roles.CEO,
    Roles.ADMIN,
    Roles.KARSAN_SSH
];

// Warranty Team Strategy - Garanti ekibi için
class WarrantyTeamStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        return warrantyTeamRoles.some(role => userRoles?.includes(role));
    }
}

// Karsan Logistics Strategy
class KarsanLogisticsStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        return userRoles?.includes(Roles.KARSAN_LOGISTICS);
    }
}

class VehicleSalesManagerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        if (userRoles?.includes(Roles.ADMIN)) return true;
        return userRoles?.includes(Roles.VEHICLE_SALES_MANAGER);
    }
}

// Sales Dealer Strategy - Yedek parça modülü ve satış bayisi için
class SalesDealerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        const salesDealerRoles = [
            Roles.SALES_DEALER,
            Roles.VEHICLE_SALES_DEALER,
            Roles.DEALER,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER,
            Roles.KARSAN_SSH
        ];
        
        return salesDealerRoles.some(role => userRoles?.includes(role));
    }
}

// Service Dealer Strategy - Servis bayisi için
class ServiceDealerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        const serviceDealerRoles = [
            Roles.SERVICE_DEALER,
            Roles.DEALER,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER
        ];
        
        return serviceDealerRoles.some(role => userRoles?.includes(role));
    }
}

// Part Team Strategy - Yedek parça ekibi için
class PartTeamStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        const partTeamRoles = [
            Roles.PART_TEAM,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER,
            Roles.DEALER,
            Roles.KARSAN_SSH
        ];
        
        return partTeamRoles.some(role => userRoles?.includes(role));
    }
}

// Warranty Quality Officer Strategy - Teknik destek talep formu için
class WarrantyQualityOfficerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        const warrantyQualityOfficerRoles = [
            Roles.WARRANTY_QUALITY_OFFICER,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER,
            Roles.DEALER,
            Roles.KARSAN_SSH
        ];
        
        return warrantyQualityOfficerRoles.some(role => userRoles?.includes(role));
    }
}

// Technical Support User Strategy - Teknik Destek Kullanıcısı için
// Bu rol SADECE Teknik Destek Talepleri modülünü görebilir.
// Admin ve WarrantyManager (SuperUser) zaten her ekrana erişebildiğinden ayrıca dahil edilir.
class TechnicalSupportUserStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;

        const technicalSupportUserRoles = [
            Roles.TECHNICAL_SUPPORT_USER,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER
        ];

        return technicalSupportUserRoles.some(role => userRoles?.includes(role));
    }
}

// Admin Only Strategy - Sadece Admin, WarrantyManager ve RegionalManager rolü için özel kontrol
// DEALER, SALES_DEALER ve SERVICE_DEALER rolleri bu ekranlara erişemez
// Bu strateji OR mantığına dahil değildir, özel kontrol olarak çalışır
class AdminOnlyStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        // Admin, WarrantyManager ve RegionalManager erişebilir
        if (userRoles?.includes(Roles.ADMIN) || 
            userRoles?.includes(Roles.WARRANTY_MANAGER) || 
            userRoles?.includes(Roles.REGIONAL_MANAGER)) return true;
        
        // DEALER, SALES_DEALER, SERVICE_DEALER ve VEHICLE_SALES_DEALER rolleri açıkça reddedilir
        const dealerRoles = [Roles.DEALER, Roles.SALES_DEALER, Roles.SERVICE_DEALER, Roles.VEHICLE_SALES_DEALER];
        if (dealerRoles.some(role => userRoles?.includes(role))) {
            return false;
        }
        
        return false;
    }
}

// Admin Strategy - Admin/WarrantyManager/RegionalManager rolü için OR mantığına dahil edilebilir kontrol
// Diğer yetkilerle birlikte kullanıldığında OR mantığı çalışır
class AdminStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        const adminRoles = [
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER,
            Roles.REGIONAL_MANAGER
        ];
        
        return adminRoles.some(role => userRoles?.includes(role));
    }
}

// Accounting Strategy - Muhasebe rolü için OR mantığına dahil edilebilir kontrol
// Accounting + Admin + WarrantyManager erişebilir
class AccountingStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;

        const accountingRoles = [
            Roles.ACCOUNTING,
            Roles.ADMIN,
            Roles.WARRANTY_MANAGER
        ];

        return accountingRoles.some(role => userRoles?.includes(role));
    }
}

// Service Bulletin User Strategy - Yalnızca yayınlanan servis bültenlerini görüntüleyen rol
class ServiceBulletinUserStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        return userRoles?.includes(Roles.SERVICE_BULLETIN_USER);
    }
}

// Finance Team Strategy - Finans rolü için OR mantığına dahil edilebilir kontrol
// Şimdilik tek/global bir finans yetkisi (FinanceTeam). İleride FinanceManager/FinanceEmployee
// gibi rollere ayrılabilir; ayrıldığında bu strategy güncellenip yeni roller eklenmeli.
// Not: Admin/WarrantyManager burada ayrıca listelenmiyor çünkü SuperUserStrategy (adım 1)
// bu rollere zaten her yere erişim veriyor; bu kontrole hiç gelmiyorlar (bkz. ServiceBulletinUserStrategy).
class FinanceTeamStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        return userRoles?.includes(Roles.FINANCE_TEAM);
    }
}

// Regular Roles Strategy
class RegularRolesStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredRoles) {
        if (!requiredRoles || requiredRoles.length === 0) return true;
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        return requiredRoles.some(role => userRoles?.includes(role));
    }
}

// Aftersales Roles Strategy
class AftersalesRolesStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredRoles, aftersalesRoles) {
        if (!requiredRoles || requiredRoles.length === 0) return true;
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        return requiredRoles.some(role => aftersalesRoles?.includes(role));
    }
}

// Super User Strategy (Admin/WarrantyManager/SSH_GENERAL_MANAGER/CEO/RegionalManager)
class SuperUserStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        const superRoles = [Roles.ADMIN, Roles.WARRANTY_MANAGER, Roles.SSH_GENERAL_MANAGER, Roles.CEO, Roles.REGIONAL_MANAGER];
        return superRoles.some(role => userRoles?.includes(role));
    }
}

// Approval Orders Dealer Strategy - Onay Bekleyen Siparişler için
// Sadece Interservice (Ana Bayi) veya Alt Bayiler (SubDealer) erişebilir
class ApprovalOrdersDealerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        
        // Interservice (Ana Bayi) VEYA Alt Bayi ise erişebilir
        return userParams?.isInterservice === true || userParams?.isSubDealer === true;
    }
}

// Domestic Dealer Strategy - Bayi Parça İade Süreci için
// Sadece yurt içi bayiler (isDomestic = true) erişebilir
class DomesticDealerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;
        
        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;
        
        // Yurt içi bayi ise erişebilir
        return userParams?.isDomestic === true;
    }
}

// Foreign Dealer Strategy - Dahili Parça Transferi için
// Sadece yurt dışı bayiler (isDomestic = false) erişebilir
class ForeignDealerStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue) {
        if (!requiredValue) return false;

        // Admin ve WarrantyManager her ekrana erişebilir
        if (userRoles?.includes(Roles.ADMIN) || userRoles?.includes(Roles.WARRANTY_MANAGER)) return true;

        // Yurt dışı bayi ise erişebilir
        return userParams?.isDomestic === false;
    }
}

// Aftersales kullanıcı bayrağı (auth.aftersales.isAftersalesUser) — backend AftersalesUser policy ile hizalı
class AftersalesUserStrategy extends PermissionStrategy {
    check(userRoles, userParams, requiredValue, isAftersalesUser) {
        if (!requiredValue) return false;
        return isAftersalesUser === true;
    }
}

// Manage Permission Strategy - Claim bazlı (manageRoles) ince granül yetki kontrolü
// "Super Admin" yetkileri için kullanılır. Rol bazlı DEĞİL, manageRoles listesi üzerinden çalışır.
// requiredValue tek bir izin (string) veya birden fazla izin (string[]) olabilir.
class ManagePermissionStrategy extends PermissionStrategy {
    check(manageRoles, requiredValue) {
        if (!requiredValue) return false;
        if (!Array.isArray(manageRoles) || manageRoles.length === 0) return false;

        const required = Array.isArray(requiredValue) ? requiredValue : [requiredValue];
        // İstenen izinlerden herhangi birine sahipse erişebilir (OR mantığı)
        return required.some((perm) => manageRoles.includes(perm));
    }
}

/**
 * Permission Checker sınıfı - Strategy pattern coordinator
 */
export class PermissionChecker {
    constructor() {
        this.strategies = {
            kmobilityUser: new KmobilityUserStrategy(),
            managePermission: new ManagePermissionStrategy(),
            CanUseFullService: new CanUseFullServiceStrategy(),
            warrantyTeam: new WarrantyTeamStrategy(),
            karsanLogistics: new KarsanLogisticsStrategy(),
            vehicleSalesManager: new VehicleSalesManagerStrategy(),
            salesDealer: new SalesDealerStrategy(),
            serviceDealer: new ServiceDealerStrategy(),
            partTeam: new PartTeamStrategy(),
            warrantyQualityOfficer: new WarrantyQualityOfficerStrategy(),
            technicalSupportUser: new TechnicalSupportUserStrategy(),
            regularRoles: new RegularRolesStrategy(),
            aftersalesRoles: new AftersalesRolesStrategy(),
            superUser: new SuperUserStrategy(),
            adminOnly: new AdminOnlyStrategy(),
            admin: new AdminStrategy(),
            accounting: new AccountingStrategy(),
            financeTeam: new FinanceTeamStrategy(),
            serviceBulletinUser: new ServiceBulletinUserStrategy(),
            approvalOrdersDealer: new ApprovalOrdersDealerStrategy(),
            domesticDealer: new DomesticDealerStrategy(),
            foreignDealer: new ForeignDealerStrategy(),
            aftersalesUser: new AftersalesUserStrategy()
        };
    }

    /**
     * Kullanıcının belirtilen yetkilere sahip olup olmadığını kontrol eder
     * @param {Object} userData - Kullanıcı verileri
     * @param {string[]} userData.roles - Kullanıcının rolleri
     * @param {string[]} userData.aftersalesRoles - Kullanıcının aftersales rolleri
     * @param {Object} userData.userParams - Kullanıcı parametreleri
     * @param {Object} userData.grants - usePermissionGrants sonucu (yetki kodu bazlı kontrol)
     * @param {Object} permissions - Gerekli yetkiler
     * @returns {Object} - { hasAccess: boolean, reason?: string }
     */
    checkPermissions(userData, permissions) {
        const { roles, aftersalesRoles, userParams, isAftersalesUser, manageRoles, grants } = userData;
        const {
            roles: requiredRoles,
            aftersalesRoles: requiredAftersalesRoles,
            requiresWarrantyTeam,
            requiresManagePermission,
            requiresKmobilityUser,
            requiresCanUseFullService,
            requiresKarsanLogistics,
            requiresVehicleSalesManager,
            requiresSalesDealer,
            requiresServiceDealer,
            requiresPartTeam,
            requiresWarrantyQualityOfficer,
            requiresTechnicalSupportUser,
            requiresAdminOnly,
            requiresAdmin,
            requiresAccounting,
            requiresFinanceTeam,
            requiresServiceBulletinUser,
            requiresApprovalOrdersDealer,
            requiresDomesticDealer,
            requiresForeignDealer,
            requiresAftersalesUser,
            excludeNonDomestic,
            excludeDealerRoles,
            excludeRoles,
            onlyRoles,
            orRoles,
            pageDisabled,
            allowedEnvironments,
            anyPermission,
            orLegacy,
            requiresPermissionApi
        } = permissions;

        // -2. pageDisabled kontrolü - Sayfa tamamen devre dışı bırakılmış, kimse erişemez (Admin dahil)
        if (pageDisabled) {
            return { hasAccess: false, reason: 'page_disabled' };
        }

        // Ortam kısıtı: ortam tanımsızsa da gizlenir (canlıda yanlışlıkla görünmesin)
        if (Array.isArray(allowedEnvironments) && !allowedEnvironments.includes(getAppEnvironment())) {
            return { hasAccess: false, reason: 'environment_restricted' };
        }

        // -1.9. Teknik Destek Kullanıcısı kısıtlaması
        // SADECE TechnicalSupportUser rolüne sahip kullanıcı yalnızca Teknik Destek Talepleri
        // modülünü görebilir. requiresTechnicalSupportUser içermeyen tüm menü/sayfalar reddedilir.
        // (isDomestic, pada5 gibi pozitif izin gerektirmeyen öğeler dahil her şey kapatılır.)
        const isOnlyTechnicalSupportUser =
            Array.isArray(roles) && roles.length > 0 &&
            roles.every((r) => r === Roles.TECHNICAL_SUPPORT_USER);
        if (isOnlyTechnicalSupportUser && !requiresTechnicalSupportUser) {
            return { hasAccess: false, reason: 'technical_support_user_restricted' };
        }

        // -1.85. Finance Team Kullanıcısı kısıtlaması
        // SADECE FinanceTeam rolüne sahip kullanıcı yalnızca Finans modülünü (ve altındaki
        // sayfaları) görebilir. requiresFinanceTeam içermeyen tüm menü/sayfalar reddedilir.
        // TechnicalSupportUser kısıtlamasıyla aynı desen — kullanıcının BAŞKA rolü de varsa
        // (ör. Admin + FinanceTeam) bu kısıtlama devreye girmez, normal yetki akışı işler.
        const isOnlyFinanceTeam =
            Array.isArray(roles) && roles.length > 0 &&
            roles.every((r) => r === Roles.FINANCE_TEAM);
        if (isOnlyFinanceTeam && !requiresFinanceTeam && !orRoles?.includes(Roles.FINANCE_TEAM)) {
            return { hasAccess: false, reason: 'finance_team_restricted' };
        }

        // Yetki API kontrolü: /v1/permission/me başarılı dönmezse (hata veya yükleniyor) erişim yok.
        // Menü gizlenir. Route yüklenirken bekler, hata olunca yetkisiz davranışa geçer.
        if (requiresPermissionApi && !grants?.isAvailable) {
            if (!grants?.isLoaded) {
                return { hasAccess: false, pending: true, reason: 'permissions_loading' };
            }
            return { hasAccess: false, reason: 'permission_api_unavailable' };
        }

        // Yetki kodu bazlı kontrol (geçiş dönemi: eski kural VEYA yetki kodu).
        // anyPermission olan öğede eski bayraklar sadece orLegacy içinde durur.
        if (Array.isArray(anyPermission) && anyPermission.length > 0) {
            if (orLegacy && this.checkPermissions(userData, orLegacy).hasAccess) {
                return { hasAccess: true, reason: 'legacy_rule' };
            }
            if (grants?.hasAny(anyPermission)) {
                return { hasAccess: true, reason: 'permission_granted' };
            }
            if (!grants?.isLoaded) {
                return { hasAccess: false, pending: true, reason: 'permissions_loading' };
            }
            return { hasAccess: false, reason: 'missing_permission' };
        }

          // -0.25. Manage Permission (claim) kontrolü - STRICT ve özel kontrol
        // Eğer requiresManagePermission tanımlıysa, SADECE manageRoles içinde ilgili izne
        // sahip kullanıcılar erişebilir. Bu kontrol SuperUser/Admin bypass'ından ÖNCE yapılır,
        // dolayısıyla Admin dahi olsa ilgili claim yoksa erişemez.
        if (requiresManagePermission) {
            if (this.strategies.managePermission.check(manageRoles, requiresManagePermission)) {
                return { hasAccess: true, reason: 'manage_permission' };
            }
            // orRoles: claim yoksa bu rollerden biri de erişim verir
            if (orRoles?.some((role) => roles?.includes(role))) {
                return { hasAccess: true, reason: 'or_role' };
            }
            return { hasAccess: false, reason: 'requires_manage_permission' };
        }
        
        // -1. excludeDealerRoles kontrolü - Bayi rollerini hariç tutar
        if (excludeDealerRoles) {
            const dealerRoles = [Roles.DEALER, Roles.SALES_DEALER, Roles.SERVICE_DEALER, Roles.VEHICLE_SALES_DEALER];
            const hasDealerRole = dealerRoles.some(role => roles?.includes(role));
            if (hasDealerRole) {
                return { hasAccess: false, reason: 'dealer_role_excluded' };
            }
        }

        // -0.75. excludeNonDomestic kontrolü - Yurt dışı kullanıcıları tamamen hariç tutar
        // isDomestic = false ise HİÇ KİMSE erişemez (Admin dahil)
        if (excludeNonDomestic && userParams?.isDomestic === false) {
            return { hasAccess: false, reason: 'non_domestic_excluded' };
        }

        // -0.5. excludeRoles kontrolü - Belirtilen rolleri hariç tutar
        if (excludeRoles && excludeRoles.length > 0) {
            const hasExcludedRole = excludeRoles.some(role => roles?.includes(role));
            if (hasExcludedRole) {
                return { hasAccess: false, reason: 'role_excluded' };
            }
        }

        // -0.4. onlyRoles - katı rol listesi; SuperUser bypass'ından önce çalışır,
        // listede olmayan rol (WarrantyManager, CEO vb. dahil) erişemez.
        if (onlyRoles && onlyRoles.length > 0) {
            const hasOnlyRole = onlyRoles.some(role => roles?.includes(role));
            if (!hasOnlyRole) {
                return { hasAccess: false, reason: 'not_in_only_roles' };
            }
        }

        // 0. Admin Only kontrolü - Bu kontrol SuperUser'dan önce yapılır
        // Eğer sadece requiresAdminOnly true ise, sadece Admin/WarrantyManager/RegionalManager erişebilir
        // Diğer yetkilerle birlikte kullanılmamalı, bunun yerine requiresAdmin kullanılmalı
        if (requiresAdminOnly && !requiresAdmin && !requiresAccounting && !requiresFinanceTeam && !requiresWarrantyTeam && !requiresKmobilityUser && !requiresCanUseFullService&&  !requiresForeignDealer &&
            !requiresKarsanLogistics && !requiresVehicleSalesManager && !requiresSalesDealer && !requiresServiceDealer && !requiresPartTeam && !requiresWarrantyQualityOfficer && !requiresTechnicalSupportUser && !requiresServiceBulletinUser && !requiresApprovalOrdersDealer && !requiresDomesticDealer && !requiresAftersalesUser) {
            if (this.strategies.adminOnly.check(roles, userParams, requiresAdminOnly)) {
                return { hasAccess: true, reason: 'admin_only' };
            } else {
                return { hasAccess: false, reason: 'requires_admin_only' };
            }
        }

        // 1. Super User kontrolü (Admin/WarrantyManager/SSH_GENERAL_MANAGER/CEO/RegionalManager her şeye erişebilir)
        if (this.strategies.superUser.check(roles, userParams)) {
            return { hasAccess: true, reason: 'super_user' };
        }

        // 2. Özel yetki kontrolleri (OR logic)
        // requiresAdmin OR mantığına dahil edilir, requiresAdminOnly ise özel kontrol olarak çalışır
        const specialPermissions = [
            { strategy: 'kmobilityUser', required: requiresKmobilityUser },
            { strategy: 'CanUseFullService', required: requiresCanUseFullService },
            { strategy: 'warrantyTeam', required: requiresWarrantyTeam },
            { strategy: 'karsanLogistics', required: requiresKarsanLogistics },
            { strategy: 'vehicleSalesManager', required: requiresVehicleSalesManager },
            { strategy: 'salesDealer', required: requiresSalesDealer },
            { strategy: 'serviceDealer', required: requiresServiceDealer },
            { strategy: 'partTeam', required: requiresPartTeam },
            { strategy: 'warrantyQualityOfficer', required: requiresWarrantyQualityOfficer },
            { strategy: 'technicalSupportUser', required: requiresTechnicalSupportUser },
            { strategy: 'admin', required: requiresAdmin },
            { strategy: 'accounting', required: requiresAccounting },
            { strategy: 'financeTeam', required: requiresFinanceTeam },
            { strategy: 'serviceBulletinUser', required: requiresServiceBulletinUser },
            { strategy: 'approvalOrdersDealer', required: requiresApprovalOrdersDealer },
            { strategy: 'domesticDealer', required: requiresDomesticDealer },
            { strategy: 'foreignDealer', required: requiresForeignDealer },
            { strategy: 'aftersalesUser', required: requiresAftersalesUser }
        ];

        const hasSpecialPermission = specialPermissions.some(({ strategy, required }) => {
            if (required) {
                if (strategy === 'aftersalesUser') {
                    return this.strategies.aftersalesUser.check(roles, userParams, required, isAftersalesUser);
                }
                return this.strategies[strategy].check(roles, userParams, required);
            }
            return false;
        });

        // Eğer özel yetki gerekliyse ve hiçbiri sağlanmadıysa
        const hasAnySpecialRequirement = requiresWarrantyTeam || requiresKmobilityUser || requiresCanUseFullService|| requiresKarsanLogistics || requiresVehicleSalesManager || requiresSalesDealer || requiresServiceDealer || requiresPartTeam || requiresWarrantyQualityOfficer || requiresTechnicalSupportUser || requiresAdmin || requiresAccounting || requiresFinanceTeam || requiresServiceBulletinUser || requiresApprovalOrdersDealer || requiresDomesticDealer ||  requiresForeignDealer  || requiresAftersalesUser;
        if (hasAnySpecialRequirement && !hasSpecialPermission) {
            return { hasAccess: false, reason: 'missing_special_permission' };
        }

        // 3. Normal roller kontrolü
        if (requiredRoles && requiredRoles.length > 0) {
            if (!this.strategies.regularRoles.check(roles, userParams, requiredRoles)) {
                return { hasAccess: false, reason: 'missing_regular_roles' };
            }
        }

        // 4. Aftersales roller kontrolü
        if (requiredAftersalesRoles && requiredAftersalesRoles.length > 0) {
            if (!this.strategies.aftersalesRoles.check(roles, userParams, requiredAftersalesRoles, aftersalesRoles)) {
                return { hasAccess: false, reason: 'missing_aftersales_roles' };
            }
        }

        return { hasAccess: true, reason: 'all_permissions_granted' };
    }

    /**
     * Menü öğesi için yetki kontrolü
     * @param {Object} userData - Kullanıcı verileri
     * @param {Object} menuItem - Menü öğesi
     * @returns {boolean}
     */
    checkMenuItemPermission(userData, menuItem) {
        if (!menuItem.permissions) {
            return true;
        }

        const result = this.checkPermissions(userData, menuItem.permissions);
        return result.hasAccess;
    }
}

// Singleton instance
export const permissionChecker = new PermissionChecker();
